export const LOOP_BILLING_ADMIN_EMAILS = ["juanquiros151@gmail.com"];

export const LOOP_BILLING_FEATURES = {
  basicBooking: { label: "Reservas básicas", group: "Core" },
  publicProfile: { label: "Perfil público", group: "Core" },
  customerCrm: { label: "Base de clientes / CRM", group: "Operación" },
  basicAnalytics: { label: "Analíticas básicas", group: "Analytics" },
  advancedAnalytics: { label: "Analíticas avanzadas", group: "Analytics" },
  inventory: { label: "Inventario", group: "Operación" },
  multiWorker: { label: "Equipo y permisos", group: "Operación" },
  whatsappManual: { label: "WhatsApp manual", group: "WhatsApp" },
  whatsappAutomation: { label: "WhatsApp automático", group: "WhatsApp" },
  onlinePayments: { label: "Pagos online", group: "Pagos" },
  loyalty: { label: "Lealtad", group: "Premium" },
  memberships: { label: "Membresías", group: "Premium" },
};

export const LOOP_BILLING_LIMITS = {
  monthlyBookings: { label: "Reservas por mes", unit: "reservas" },
  workers: { label: "Trabajadores", unit: "personas" },
  services: { label: "Servicios", unit: "servicios" },
  whatsappMessages: { label: "WhatsApp mensuales", unit: "mensajes" },
};

const PLAN_DEFAULTS = {
  free: {
    label: "Plan gratis",
    monthlyPrice: 0,
    features: {
      basicBooking: true,
      publicProfile: true,
      customerCrm: true,
      basicAnalytics: true,
      advancedAnalytics: false,
      inventory: false,
      multiWorker: true,
      whatsappManual: true,
      whatsappAutomation: false,
      onlinePayments: false,
      loyalty: false,
      memberships: false,
    },
    limits: { monthlyBookings: 100, workers: 3, services: 20, whatsappMessages: 50 },
  },
  pro: {
    label: "Pro",
    monthlyPrice: 29,
    features: {
      basicBooking: true,
      publicProfile: true,
      customerCrm: true,
      basicAnalytics: true,
      advancedAnalytics: true,
      inventory: true,
      multiWorker: true,
      whatsappManual: true,
      whatsappAutomation: false,
      onlinePayments: true,
      loyalty: false,
      memberships: false,
    },
    limits: { monthlyBookings: 500, workers: 8, services: 60, whatsappMessages: 150 },
  },
  premium: {
    label: "Premium",
    monthlyPrice: 59,
    features: {
      basicBooking: true,
      publicProfile: true,
      customerCrm: true,
      basicAnalytics: true,
      advancedAnalytics: true,
      inventory: true,
      multiWorker: true,
      whatsappManual: true,
      whatsappAutomation: true,
      onlinePayments: true,
      loyalty: true,
      memberships: true,
    },
    limits: { monthlyBookings: 1500, workers: 20, services: 200, whatsappMessages: 500 },
  },
  enterprise: {
    label: "Enterprise",
    monthlyPrice: null,
    features: Object.keys(LOOP_BILLING_FEATURES).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    limits: { monthlyBookings: -1, workers: -1, services: -1, whatsappMessages: -1 },
  },
};

function normalizeDateLike(value) {
  try {
    if (!value) return null;
    if (typeof value.toDate === "function") return value.toDate();
    if (value.seconds) return new Date(Number(value.seconds) * 1000);
    if (typeof value === "string") {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  } catch (_) {}
  return null;
}

function dateInputValue(value) {
  const d = normalizeDateLike(value);
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function isLoopBillingAdminEmail(email) {
  return LOOP_BILLING_ADMIN_EMAILS.includes(String(email || "").trim().toLowerCase());
}

export function getLoopBillingPlanDefaults(plan = "free") {
  return PLAN_DEFAULTS[String(plan || "free").toLowerCase()] || PLAN_DEFAULTS.free;
}

export function normalizeBusinessBilling(rawBilling = {}, businessDoc = {}) {
  const source = rawBilling && typeof rawBilling === "object" ? rawBilling : {};
  const plan = String(source.plan || businessDoc.billingPlan || "free").trim().toLowerCase();
  const planDefaults = getLoopBillingPlanDefaults(plan);
  const earlyAccess = source.earlyAccess === true || businessDoc.earlyAccess === true;
  const status = String(source.status || businessDoc.billingStatus || (earlyAccess ? "trialing" : "active")).trim().toLowerCase();

  const baseFeatures = { ...(planDefaults.features || {}) };
  const overrideFeatures = source.features && typeof source.features === "object" ? source.features : {};
  const features = Object.keys(LOOP_BILLING_FEATURES).reduce((acc, key) => {
    acc[key] = typeof overrideFeatures[key] === "boolean" ? overrideFeatures[key] : baseFeatures[key] === true;
    return acc;
  }, {});

  if (earlyAccess && source.earlyAccessUnlockAll === true) {
    Object.keys(features).forEach((key) => { features[key] = true; });
  }

  const baseLimits = { ...(planDefaults.limits || {}) };
  const overrideLimits = source.limits && typeof source.limits === "object" ? source.limits : {};
  const limits = Object.keys(LOOP_BILLING_LIMITS).reduce((acc, key) => {
    const n = Number(overrideLimits[key]);
    acc[key] = Number.isFinite(n) ? n : Number(baseLimits[key] ?? 0);
    return acc;
  }, {});

  return {
    plan: PLAN_DEFAULTS[plan] ? plan : "free",
    planLabel: planDefaults.label,
    monthlyPrice: planDefaults.monthlyPrice,
    status,
    earlyAccess,
    earlyAccessUnlockAll: source.earlyAccessUnlockAll === true,
    earlyAccessGrantedBy: source.earlyAccessGrantedBy || businessDoc.earlyAccessGrantedBy || null,
    earlyAccessEndsAt: source.earlyAccessEndsAt || businessDoc.earlyAccessEndsAt || null,
    billingProvider: source.billingProvider || businessDoc.billingProvider || "none",
    enforceFeatureGates: source.enforceFeatureGates === true,
    notes: source.notes || "",
    features,
    limits,
  };
}

export function businessHasFeature(businessDoc = {}, featureKey) {
  if (!featureKey) return false;
  const billing = normalizeBusinessBilling(businessDoc.billing || {}, businessDoc);
  return billing.features && billing.features[featureKey] === true;
}

export function billingDateInputValue(value) {
  return dateInputValue(value);
}

export function billingDateFromInput(value) {
  const text = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
  return new Date(`${text}T23:59:59-05:00`).toISOString();
}
